// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import * as Allocation from '@digitalasset/cn-allocation-api-ts-client';
import * as TokenMetadata from '@digitalasset/cn-token-metadata-api-ts-client';
import React, { useMemo } from 'react';
import { BaseApiMiddleware } from '../utils/BaseApiMiddleware';
import { OpenAPILoggingMiddleware } from '../utils/OpenApiLoggingMiddleware';
import { CNAllocationApiContext } from './CNAllocationApiContext';
import { CNTokenMetadataApiContext } from './CNTokenMetadataApiContext';
import { useUserState } from './UserContext';

export type CNAllocationApiProviderProps = {
  url: string;
};

export const CNAllocationApiProvider = ({
  url,
  children,
}: React.PropsWithChildren<CNAllocationApiProviderProps>) => {
  const { loggedInUser } = useUserState();
  const selectedToken = loggedInUser?.userAccessToken ?? '';

  const allocationClient: Allocation.DefaultApi | undefined = useMemo(() => {
    const configuration = Allocation.createConfiguration({
      baseServer: new Allocation.ServerConfiguration(url, {}),
      promiseMiddleware: [
        new BaseApiMiddleware<Allocation.RequestContext, Allocation.ResponseContext>(selectedToken),
        new OpenAPILoggingMiddleware('scan-proxy'),
      ],
    });

    return new Allocation.DefaultApi(configuration);
  }, [selectedToken, url]);

  const tokenMetadataClient: TokenMetadata.DefaultApi | undefined = useMemo(() => {
    const configuration = TokenMetadata.createConfiguration({
      baseServer: new TokenMetadata.ServerConfiguration(url, {}),
      promiseMiddleware: [
        new BaseApiMiddleware<TokenMetadata.RequestContext, TokenMetadata.ResponseContext>(
          selectedToken,
        ),
        new OpenAPILoggingMiddleware('scan-proxy'),
      ],
    });

    return new TokenMetadata.DefaultApi(configuration);
  }, [selectedToken, url]);

  return (
    <CNAllocationApiContext.Provider value={allocationClient}>
      <CNTokenMetadataApiContext.Provider value={tokenMetadataClient}>
        {children}
      </CNTokenMetadataApiContext.Provider>
    </CNAllocationApiContext.Provider>
  );
};
